<script setup>
import { computed, ref, watch } from "vue";

import BeneficiaryContent from "@/components/beneficiary/drawer/DrawerContent";
import AcademyContent from "@/components/academy/drawer/DrawerContent";
import CompanyContent from "@/components/company/drawer/DrawerContent";
import JobPositionContent from "@/components/job-position/drawer/DrawerContent";
import EmployabilityContent from "@/components/employability/drawer/DrawerContent";
import LivelihoodContent from "@/components/livelihood/drawer/DrawerContent";
import {
  useAcademyStore,
  useBeneficiaryStore,
  useCompanyStore,
  useEmployabilityStore,
  useGlobalStore,
  useJobPositionStore,
  useLivelihoodStore,
} from "@/stores";

const globalStore = useGlobalStore();
const beneficiaryStore = useBeneficiaryStore();
const academyStore = useAcademyStore();
const companyStore = useCompanyStore();
const jobPositionStore = useJobPositionStore();
const employabilityStore = useEmployabilityStore();
const livelihood = useLivelihoodStore();

const loading = ref(false);

const drawer = computed({
  get: () => globalStore.drawer,
  set: (val) => (globalStore.drawer = val),
});

const show = computed({
  get: () => globalStore.drawer?.show,
  set: (val) => (globalStore.drawer.show = val),
});

const closeDrawer = () => {
  drawer.value.show = false;
};

const isDrawerOpen = ref(false);

const handleBackClick = () => {
  const item = drawer.value.history[drawer.value.history.length - 2];

  if (item) {
    drawer.value.show = true;
    drawer.value.content = item;
    drawer.value.history.pop();
  }
};

const data = ref({});

const fetchData = async () => {
  if (drawer.value.content.type === "beneficiary") {
    loading.value = true;
    data.value = await beneficiaryStore.getBeneficiary({
      id: drawer.value.content.id,
    });
    loading.value = false;
  }

  if (drawer.value.content.type === "academy") {
    loading.value = true;
    data.value = await academyStore.getCourse({
      id: drawer.value.content.id,
    });
    loading.value = false;
  }

  if (drawer.value.content.type === "company") {
    loading.value = true;
    data.value = await companyStore.getCompany({
      id: drawer.value.content.id,
    });
    loading.value = false;
  }

  if (drawer.value.content.type === "job-position") {
    loading.value = true;
    data.value = await jobPositionStore.getJobPosition({
      id: drawer.value.content.id,
    });
    loading.value = false;
  }

  if (drawer.value.content.type === "employability") {
    loading.value = true;
    data.value = await employabilityStore.getEmployability({
      id: drawer.value.content.id,
    });
    loading.value = false;
  }

  if (drawer.value.content.type === "livelihood") {
    loading.value = true;
    data.value = await livelihood.getLivelihood({
      id: drawer.value.content.id,
    });
    loading.value = false;
  }
};

watch(
  () => drawer.value.content.id,
  async (newId) => {
    if (drawer.value.show && newId) {
      await fetchData();
    }
  }
);

watch(show, async (newValue) => {
  if (newValue) {
    await fetchData();
    isDrawerOpen.value = true;
  } else {
    isDrawerOpen.value = false;
    drawer.value.history = [];
  }
});

watch(isDrawerOpen, (newValue) => {
  if (!newValue) {
    drawer.value.show = false;
    drawer.value.content = { type: null, id: null };
  }
});
</script>

<template>
  <v-navigation-drawer
    v-model="isDrawerOpen"
    location="right"
    width="1080"
    temporary
  >
    <div class="d-flex justify-space-between justify-sm-end mt-5 ml-5 mr-5">
      <v-btn
        v-if="drawer?.history?.length > 1"
        variant="text"
        size="small"
        @click="handleBackClick"
      >
        <v-icon>mdi-chevron-left</v-icon>Go Back
      </v-btn>

      <div v-else />

      <v-btn variant="text" size="small" @click="closeDrawer">
        <v-icon>mdi-close-circle</v-icon> close
      </v-btn>
    </div>

    <BeneficiaryContent
      v-if="drawer.content.type == 'beneficiary' && !loading"
      :beneficiary="data"
    />
    <AcademyContent
      v-if="drawer.content.type == 'academy' && !loading"
      :course="data"
    />
    <CompanyContent
      v-if="drawer.content.type == 'company' && !loading"
      :company="data"
    />
    <JobPositionContent
      v-if="drawer.content.type == 'job-position' && !loading"
      :job="data"
    />
    <EmployabilityContent
      v-if="drawer.content.type == 'employability' && !loading"
      :employability="data"
    />
    <LivelihoodContent
      v-if="drawer.content.type == 'livelihood' && !loading"
      :livelihood="data"
    />
  </v-navigation-drawer>
</template>
